import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../../ui/button';
import FundingPanel from '../FundingPanel';
import FundingApplicationHistoryTable from './FundingApplicationHistoryTable';
import FundingApplicationRecordDrawer from './FundingApplicationRecordDrawer';
import { buildApplicantPillsFromDraft } from './fundingApplicationDisplay';
import { getEntityById } from './fundingClientEntities';
import FundingClientApplicationDashboard from './FundingClientApplicationDashboard';
import FundingManageFieldsPanel from './FundingManageFieldsPanel';
import {
  loadApplicationHistoryColumns,
  saveApplicationHistoryColumns,
} from './applicationHistoryColumnConfig';
import { computeMockMetrics, emptyApplicationMetrics, MOCK_APPLICATIONS } from './fundingWorkspaceMockData';
import {
  applicationToPayload,
  createClientApplication,
  fetchApplicationMetrics,
  fetchClientApplications,
  isFundingApplicationDebugEnabled,
  updateClientApplication,
} from '../../../services/funding/fundingApplicationsService';
import { resolveSelectedClientId } from '../../../services/funding/fundingClientsService';
import FundingClientDocumentsTab from './FundingClientDocumentsTab';
import FundingClientNotesTab from './FundingClientNotesTab';
import FundingClientIntakeProfilePanel from './FundingClientIntakeProfilePanel';
import BrowserProfilesPanel from './BrowserProfilesPanel';
import { isFundingBrowserProfilesEnabled } from '../../../services/funding/fundingBrowserProfilesService';
import { useAuth } from '../../../context/AuthContext';
import {
  FundingApplicationDashboardSkeleton,
  useSkeletonFailsafe,
} from './FundGeniusSkeletons';
import { isFrontendDevAuthBypassEnabled } from '../../../utils/devAuth';

const BASE_WORKSPACE_TABS = ['Application Info', 'Client Info', 'Documents', 'Notes'];
const CLIENT_INFO_TAB = 'Client Info';
const BROWSER_PROFILES_TAB = 'Browser Profiles';

export const WORKSPACE_TAB_ORDER = [...BASE_WORKSPACE_TABS, BROWSER_PROFILES_TAB];

export const REMOVED_WORKSPACE_TABS = ['Overview', 'Intake', 'Readiness', 'Lender Matches', 'Application History'];

const normalizeApplicationDraft = (draft, existing, client) => {
  const id = existing?.id || `mock-app-${Date.now()}`;
  const masked = draft.ssnMasked || existing?.ssnMasked || '';
  const last4 = masked.replace(/\D/g, '').slice(-4) || existing?.ssnLast4;
  const entity = getEntityById(client, draft.entityId);
  const applicantPills = buildApplicantPillsFromDraft(draft.applicants || [], entity);
  return {
    ...existing,
    ...draft,
    id,
    entityId: draft.entityId ?? existing?.entityId,
    entityLabel: draft.entityLabel || entity?.businessLegalName || existing?.entityLabel,
    applicantOwnerIds: draft.applicantOwnerIds ?? existing?.applicantOwnerIds ?? [],
    applicants: draft.applicants ?? existing?.applicants ?? [],
    applicantPills,
    source: draft.source || existing?.source || 'manual',
    uploadedFileName: draft.uploadedFileName ?? existing?.uploadedFileName ?? '',
    scanStatus: draft.scanStatus ?? existing?.scanStatus ?? '',
    extractionConfidence: draft.extractionConfidence ?? existing?.extractionConfidence ?? {},
    approvalAmount: draft.approvalAmount !== '' && draft.approvalAmount != null ? Number(draft.approvalAmount) : null,
    personalIncome: draft.personalIncome !== '' && draft.personalIncome != null ? Number(draft.personalIncome) : null,
    businessIncome: draft.businessIncome !== '' && draft.businessIncome != null ? Number(draft.businessIncome) : null,
    housing: draft.housing !== '' && draft.housing != null ? Number(draft.housing) : null,
    businessExpenses: draft.businessExpenses !== '' && draft.businessExpenses != null ? Number(draft.businessExpenses) : null,
    savings: draft.savings !== '' && draft.savings != null ? Number(draft.savings) : null,
    retirementAccount: draft.retirementAccount !== '' && draft.retirementAccount != null ? Number(draft.retirementAccount) : null,
    experianScoreUsed: draft.experianScoreUsed ?? existing?.experianScoreUsed,
    transunionScoreUsed: draft.transunionScoreUsed ?? existing?.transunionScoreUsed,
    equifaxScoreUsed: draft.equifaxScoreUsed ?? existing?.equifaxScoreUsed,
    selectedBureauPull: draft.selectedBureauPull ?? existing?.selectedBureauPull ?? null,
    bureauPullScores: draft.bureauPullScores ?? existing?.bureauPullScores,
    ssnLast4: last4 || existing?.ssnLast4,
    ssnMasked: last4 ? `***-**-${last4}` : masked || existing?.ssnMasked,
    einMasked: draft.einMasked || draft.fullEin || existing?.einMasked,
    employerName: draft.employerName ?? draft.employer,
  };
};

const FundingClientWorkspace = forwardRef(({
  client,
  agencyId,
  initialTab = 'Application Info',
  onRosterStatsChange,
  embedded = false,
  selectedEntityId,
  onOpenIdentity,
  onEditClient,
}, ref) => {
  const { user } = useAuth();
  const anchorRef = useRef(null);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [applications, setApplications] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [useMock, setUseMock] = useState(false);

  const [columnConfig, setColumnConfig] = useState(() => loadApplicationHistoryColumns());
  const [manageFieldsOpen, setManageFieldsOpen] = useState(false);
  const [historyEntityFilter, setHistoryEntityFilter] = useState('all');

  const [recordDrawerOpen, setRecordDrawerOpen] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [recordEditStart, setRecordEditStart] = useState(false);
  const [savingRecord, setSavingRecord] = useState(false);
  const [clientIdError, setClientIdError] = useState(null);

  const selectedClientId = resolveSelectedClientId(client);
  const isSampleClient = Boolean(client?.isSample);
  const showBrowserProfilesTab = isFundingBrowserProfilesEnabled();
  const workspaceTabs = useMemo(
    () => (showBrowserProfilesTab
      ? [...BASE_WORKSPACE_TABS, BROWSER_PROFILES_TAB]
      : BASE_WORKSPACE_TABS),
    [showBrowserProfilesTab],
  );

  const workspaceClient = useMemo(() => {
    const persistedId = selectedClientId
      || client?.id
      || client?.clientId
      || client?.client_id
      || client?.fundingClientId
      || client?.funding_client_id
      || client?._id
      || null;

    return {
      ...client,
      id: persistedId,
      clientId: persistedId,
      client_id: persistedId,
      selectedEntityId: selectedEntityId || client?.selectedEntityId,
    };
  }, [client, selectedClientId, selectedEntityId]);

  useEffect(() => {
    console.log('[BrowserProfilesTab]', {
      enabled: isFundingBrowserProfilesEnabled(),
      selectedClientId,
      workspaceClientId: workspaceClient?.id,
      userRole: user?.role,
      showBrowserProfilesTab,
    });
  }, [selectedClientId, showBrowserProfilesTab, user?.role, workspaceClient?.id]);

  const publishRosterStats = useCallback((apps, metricData) => {
    if (!selectedClientId || !onRosterStatsChange) return;
    const lastApp = apps[0];
    onRosterStatsChange(selectedClientId, {
      lastApplicationDate: lastApp?.dateOfApplication || null,
      totalApprovalAmount: metricData?.totalApprovalAmount ?? 0,
      totalDenied: metricData?.totalDenied ?? 0,
    });
  }, [selectedClientId, onRosterStatsChange]);

  const applyApplications = useCallback((apps, metricData) => {
    setApplications(apps);
    setMetrics(metricData);
    publishRosterStats(apps, metricData);
  }, [publishRosterStats]);

  const loadWorkspace = useCallback(async () => {
    if (!agencyId) return;

    const applySampleMockData = () => {
      const mockMetrics = computeMockMetrics(MOCK_APPLICATIONS);
      applyApplications(MOCK_APPLICATIONS, mockMetrics);
      setUseMock(true);
      setClientIdError(null);
      if (isFundingApplicationDebugEnabled()) {
        console.info('[FundingApplications] KPI mode: sample_client', {
          clientId: selectedClientId,
          isSample: true,
          applicationCount: MOCK_APPLICATIONS.length,
        });
      }
    };

    const applyRealEmptyState = (errorMessage = null) => {
      applyApplications([], emptyApplicationMetrics());
      setUseMock(false);
      setClientIdError(errorMessage);
      if (isFundingApplicationDebugEnabled()) {
        console.info('[FundingApplications] KPI mode: real_client_empty', {
          clientId: selectedClientId,
          isSample: false,
          applicationCount: 0,
        });
      }
    };

    if (!selectedClientId) {
      if (isSampleClient) {
        applySampleMockData();
      } else {
        applyRealEmptyState('Unable to load client applications because the client ID is missing.');
      }
      setLoading(false);
      return;
    }

    setClientIdError(null);

    if (isSampleClient && isFrontendDevAuthBypassEnabled()) {
      applySampleMockData();
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const [appList, metricData] = await Promise.all([
        fetchClientApplications(selectedClientId, agencyId),
        fetchApplicationMetrics(selectedClientId, agencyId),
      ]);

      if (isSampleClient && (!Array.isArray(appList) || appList.length === 0)) {
        applySampleMockData();
      } else if (!isSampleClient) {
        applyApplications(appList || [], metricData || emptyApplicationMetrics());
        setUseMock(false);
        if (isFundingApplicationDebugEnabled()) {
          console.info('[FundingApplications] KPI mode: real_client_data', {
            clientId: selectedClientId,
            isSample: false,
            applicationCount: (appList || []).length,
          });
        }
      } else {
        applyApplications(appList || [], metricData || emptyApplicationMetrics());
        setUseMock(false);
      }
    } catch (err) {
      if (isSampleClient) {
        console.warn('Funding workspace API fallback to sample mock data', err);
        applySampleMockData();
      } else {
        console.warn('Funding workspace failed to load client applications', err);
        applyRealEmptyState();
        toast.error(err?.message || 'Could not load application history.');
      }
    } finally {
      setLoading(false);
    }
  }, [selectedClientId, agencyId, isSampleClient, applyApplications]);

  useEffect(() => {
    if (!workspaceTabs.includes(activeTab)) {
      setActiveTab('Application Info');
    }
  }, [activeTab, workspaceTabs]);

  useEffect(() => {
    loadWorkspace();
  }, [loadWorkspace]);

  useEffect(() => {
    if (initialTab) setActiveTab(initialTab);
  }, [initialTab, selectedClientId]);

  useEffect(() => {
    setHistoryEntityFilter('all');
  }, [selectedClientId, selectedEntityId]);

  const openRecord = (app, edit = false) => {
    setSelectedApplication(app);
    setRecordEditStart(edit);
    setRecordDrawerOpen(true);
  };

  const openNewApplication = () => {
    setActiveTab('Application Info');
    setSelectedApplication(null);
    setRecordEditStart(true);
    setRecordDrawerOpen(true);
    anchorRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  };

  const focusApplicationInfo = () => {
    setActiveTab('Application Info');
    anchorRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  };

  useImperativeHandle(ref, () => ({
    openNewApplication,
    focusApplicationInfo,
    reload: loadWorkspace,
  }));

  const handleSaveApplication = async (draft) => {
    if (!selectedClientId || !agencyId) return;

    if (useMock) {
      const normalized = normalizeApplicationDraft(draft, selectedApplication, workspaceClient);
      const nextApps = selectedApplication?.id
        ? applications.map((item) => (item.id === selectedApplication.id ? normalized : item))
        : [normalized, ...applications];
      const nextMetrics = computeMockMetrics(nextApps);
      applyApplications(nextApps, nextMetrics);
      toast.success(selectedApplication?.id ? 'Application updated' : 'Application created');
      setRecordDrawerOpen(false);
      return;
    }

    setSavingRecord(true);
    try {
      const payload = applicationToPayload(draft);
      if (selectedApplication?.id) {
        await updateClientApplication(selectedClientId, agencyId, selectedApplication.id, payload);
        toast.success('Application updated');
      } else {
        await createClientApplication(selectedClientId, agencyId, payload);
        toast.success('Application created');
      }
      setRecordDrawerOpen(false);
      await loadWorkspace();
    } catch (err) {
      toast.error(err?.message || 'Failed to save application');
    } finally {
      setSavingRecord(false);
    }
  };

  const handleDuplicateApplication = (application) => {
    if (!application) return;
    const clone = {
      ...application,
      id: undefined,
      decision: 'Pending',
      dateOfApplication: new Date().toISOString().slice(0, 10),
    };
    delete clone.createdAt;
    delete clone.updatedAt;
    setSelectedApplication(clone);
    setRecordEditStart(true);
    setRecordDrawerOpen(true);
    toast.success('Application duplicated — save to create a new record');
  };

  const isApplicationsPending = Boolean(client) && loading && metrics === null;
  const applicationsSkeletonTimedOut = useSkeletonFailsafe(isApplicationsPending);
  const shouldShowApplicationSkeleton = isApplicationsPending && !applicationsSkeletonTimedOut;

  if (!client) {
    if (embedded) return null;
    return (
      <FundingPanel title="Selected client workspace" subtitle="Select a client from the roster to open the command center.">
        <div className="rounded-xl border border-dashed border-[var(--fg-line)] bg-[var(--fg-panel-soft)] px-4 py-10 text-center text-sm text-[var(--fg-muted)]">
          No client selected. Click a client row or use Application Info to begin.
        </div>
      </FundingPanel>
    );
  }

  const showApplicationInfo = activeTab === 'Application Info';
  const showClientInfo = activeTab === CLIENT_INFO_TAB;
  const showBrowserProfiles = activeTab === BROWSER_PROFILES_TAB && showBrowserProfilesTab;

  const applicationInfoBody = shouldShowApplicationSkeleton ? (
    <FundingApplicationDashboardSkeleton />
  ) : (
    <>
      <FundingClientApplicationDashboard metrics={metrics} />
      {clientIdError ? (
        <p className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="client-applications-id-error">
          {clientIdError}
        </p>
      ) : null}
      <div className="fg-section-head">
        <div>
          <h4>Application History</h4>
          <p>Copy any field or open a record to edit.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" className="rounded-xl" onClick={() => setManageFieldsOpen(true)}>
            Manage Fields
          </Button>
          <Button className="rounded-xl bg-[var(--fg-accent)] hover:bg-[#126b4f]" onClick={openNewApplication}>
            Add Application
          </Button>
        </div>
      </div>
      <FundingApplicationHistoryTable
        applications={applications}
        columnConfig={columnConfig}
        clientId={selectedClientId}
        agencyId={agencyId}
        client={workspaceClient}
        entityFilter={historyEntityFilter}
        onEntityFilterChange={setHistoryEntityFilter}
        onOpenRecord={openRecord}
      />
    </>
  );

  const workspaceBody = (
    <>
      {!embedded ? (
        <div className="fg-workspace-action-row">
          <Button variant="outline" className="min-w-[190px] rounded-xl">Export client history</Button>
          <Button className="min-w-[190px] rounded-xl bg-[var(--fg-accent)] hover:bg-[#126b4f]" onClick={openNewApplication}>
            New Application
          </Button>
        </div>
      ) : null}

      <div className="fg-client-tabs" data-testid="funding-workspace-tabs">
        {workspaceTabs.map((tab) => (
          <button
            key={tab}
            type="button"
            data-testid={`workspace-tab-${tab.replace(/\s+/g, '-').toLowerCase()}`}
            data-active={activeTab === tab}
            onClick={() => setActiveTab(tab)}
            className="fg-client-tab"
          >
            {tab}
          </button>
        ))}
      </div>

      {showApplicationInfo ? applicationInfoBody : null}

      {showClientInfo ? (
        <div className="fg-client-info-tab" data-testid="client-info-tab">
          <FundingClientIntakeProfilePanel
            client={workspaceClient}
            clientId={selectedClientId}
            agencyId={agencyId}
            onEdit={onEditClient}
          />
        </div>
      ) : null}

      {activeTab === 'Documents' ? <FundingClientDocumentsTab /> : null}
      {activeTab === 'Notes' ? <FundingClientNotesTab /> : null}
      {showBrowserProfiles ? (
        <BrowserProfilesPanel client={workspaceClient} agencyId={agencyId} />
      ) : null}
    </>
  );

  if (embedded) {
    return (
      <>
        <div ref={anchorRef} data-testid="funding-client-workspace" className="fg-workspace-embedded">
          <div className="fg-workspace-full">{workspaceBody}</div>
        </div>
        <FundingApplicationRecordDrawer
          open={recordDrawerOpen}
          onOpenChange={setRecordDrawerOpen}
          application={selectedApplication}
          client={workspaceClient}
          clientId={selectedClientId}
          agencyId={agencyId}
          startInEdit={recordEditStart}
          onSave={handleSaveApplication}
          onDuplicate={handleDuplicateApplication}
          onRequestEdit={(app) => {
            setSelectedApplication(app);
            setRecordEditStart(true);
            setRecordDrawerOpen(true);
          }}
          saving={savingRecord}
          onOpenIdentity={onOpenIdentity}
        />
        <FundingManageFieldsPanel
          open={manageFieldsOpen}
          onOpenChange={setManageFieldsOpen}
          columnConfig={columnConfig}
          onApply={(next) => {
            setColumnConfig(next);
            saveApplicationHistoryColumns(next);
            toast.success('Table columns updated');
          }}
        />
      </>
    );
  }

  return (
    <>
      <div ref={anchorRef} data-testid="funding-client-workspace">
        <FundingPanel
          className="mt-6"
          title="Selected client workspace"
          subtitle="Track performance, approvals, product totals, and application data for the selected client."
        >
          <div className="fg-workspace-full">{workspaceBody}</div>
        </FundingPanel>
      </div>

      <FundingApplicationRecordDrawer
        open={recordDrawerOpen}
        onOpenChange={setRecordDrawerOpen}
        application={selectedApplication}
        client={client}
        clientId={selectedClientId}
        agencyId={agencyId}
        startInEdit={recordEditStart}
        onSave={handleSaveApplication}
        onDuplicate={handleDuplicateApplication}
        onRequestEdit={(app) => {
          setSelectedApplication(app);
          setRecordEditStart(true);
          setRecordDrawerOpen(true);
        }}
        saving={savingRecord}
        onOpenIdentity={onOpenIdentity}
      />

      <FundingManageFieldsPanel
        open={manageFieldsOpen}
        onOpenChange={setManageFieldsOpen}
        columnConfig={columnConfig}
        onApply={(next) => {
          setColumnConfig(next);
          saveApplicationHistoryColumns(next);
          toast.success('Table columns updated');
        }}
      />
    </>
  );
});

FundingClientWorkspace.displayName = 'FundingClientWorkspace';

export default FundingClientWorkspace;
